/**
 * Meldet Browserfehler an die PHQ-eigene Diagnose-Route des SMS-PRO-BFF
 * (POST /api/v1/phq/diagnose/client-errors, Antwort immer 204).
 *
 * Grundsatz: scheitert STILL. Kein Wurf, kein Logger-Aufruf im Fehlerpfad
 * (sonst Rekursion/Log-Spam), kein Wiederholungsversuch. Der Versand nutzt
 * bewusst direkt `fetch` und nicht den apiClient (kein Logging, kein
 * 401-Refresh, keine Session-Events).
 */
import { getRecentCorrelationIds } from './correlation';
import { logger } from './logger';

export interface DiagnoseInput {
  level?: 'error' | 'warn';
  message: string;
  stack?: string;
  componentStack?: string;
  correlationId?: string;
  context?: Record<string, unknown>;
}

interface DiagnoseEntry {
  level: 'error' | 'warn';
  message: string;
  stack?: string;
  componentStack?: string;
  route?: string;
  url?: string;
  correlationId?: string;
  browser?: string;
  buildVersion?: string;
  occurredAt: string;
  context?: Record<string, unknown>;
}

export const DIAGNOSE_ENDPOINT = '/api/v1/phq/diagnose/client-errors';

/**
 * Eingeschaltet: Die Route ist die eigene PHQ-Route im SMS-PRO-BFF und hat eine
 * eigene Ablage, getrennt von der Diagnose der SMS-PRO-Oberfläche.
 */
export const diagnoseSchalter = { aktiv: true };
const MAX_PER_CALL = 20;
const FLUSH_DELAY_MS = 5000;
/** BFF erlaubt 60/h je Nutzer; wir bleiben mit Reserve darunter. */
const MAX_PER_HOUR = 50;
const HOUR_MS = 3_600_000;
const DEDUPE_MS = 60_000;
const QUEUE_MAX = 100;
/** BFF-Grenze 256 KB; wir zielen auf maximal 200 KB. */
const MAX_BODY_BYTES = 200_000;
/** Browser-Grenze für keepalive-Bodies ist 64 KB. */
const MAX_KEEPALIVE_BYTES = 60_000;
const LIMITS = { message: 1000, stack: 6000, componentStack: 3000, url: 500, route: 300, browser: 300 };

let queue: DiagnoseEntry[] = [];
let sentTimes: number[] = [];
const recentKeys = new Map<string, number>();
let timer: ReturnType<typeof setTimeout> | null = null;
let installed = false;

function cut(s: string | undefined, max: number): string | undefined {
  if (s === undefined || s === null) return undefined;
  const str = String(s);
  return str.length > max ? str.slice(0, max) : str;
}

/** `/projects/<uuid>/kanban` -> `/projects/:id/kanban`; Query/Hash entfallen. */
export function toRoutePattern(pathname: string): string {
  return pathname
    .split('?')[0]!
    .split('#')[0]!
    .split('/')
    .map((seg) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg) ||
      /^\d+$/.test(seg) ||
      /^[0-9a-f]{16,}$/i.test(seg)
        ? ':id'
        : seg,
    )
    .join('/');
}

function isSendable(): boolean {
  try {
    return !!localStorage.getItem('pm_token') && !!localStorage.getItem('pm_tenant');
  } catch {
    return false;
  }
}

function buildHeaders(correlationId?: string): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  try {
    const token = localStorage.getItem('pm_token');
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const tenant = localStorage.getItem('pm_tenant');
    if (tenant) headers['X-Tenant-Id'] = tenant;
  } catch { /* ignore */ }
  if (correlationId) headers['X-Correlation-Id'] = correlationId;
  return headers;
}

/** Correlation-ID des jüngsten fehlgeschlagenen API-Aufrufs (max. 10 s alt). */
function lastFailedCorrelationId(): string | undefined {
  try {
    const known = new Set(getRecentCorrelationIds());
    const buf = logger.getBuffer();
    const now = Date.now();
    for (let i = buf.length - 1; i >= 0; i--) {
      const e = buf[i]!;
      if (now - Date.parse(e.ts) > 10_000) break;
      if (e.category === 'api' && e.level === 'error' && e.correlationId && known.has(e.correlationId)) {
        return e.correlationId;
      }
    }
  } catch { /* ignore */ }
  return undefined;
}

function scheduleFlush(): void {
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    void flushDiagnose();
  }, FLUSH_DELAY_MS);
}

/** Nimmt einen Fehler entgegen. Wirft nie. */
export function reportClientError(input: DiagnoseInput): void {
  try {
    if (!diagnoseSchalter.aktiv || !isSendable()) return;
    const stackLine = input.stack?.split('\n')[1]?.trim() ?? '';
    const key = `${input.message}|${stackLine}`;
    const now = Date.now();
    const seen = recentKeys.get(key);
    if (seen !== undefined && now - seen < DEDUPE_MS) return;
    recentKeys.set(key, now);
    if (recentKeys.size > 200) {
      for (const [k, t] of recentKeys) if (now - t >= DEDUPE_MS) recentKeys.delete(k);
    }
    if (queue.length >= QUEUE_MAX) return;

    const loc = typeof window !== 'undefined' ? window.location : undefined;
    queue.push({
      level: input.level ?? 'error',
      message: cut(input.message, LIMITS.message) ?? '',
      stack: cut(input.stack, LIMITS.stack),
      componentStack: cut(input.componentStack, LIMITS.componentStack),
      route: loc ? cut(toRoutePattern(loc.pathname), LIMITS.route) : undefined,
      url: loc ? cut(`${loc.origin}${loc.pathname}`, LIMITS.url) : undefined,
      correlationId: input.correlationId ?? lastFailedCorrelationId(),
      browser: typeof navigator !== 'undefined' ? cut(navigator.userAgent, LIMITS.browser) : undefined,
      buildVersion: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : undefined,
      occurredAt: new Date(now).toISOString(),
      context: input.context,
    });
    if (queue.length >= MAX_PER_CALL) void flushDiagnose();
    else scheduleFlush();
  } catch { /* still */ }
}

/** Sendet bis zu 20 Einträge. `keepalive` für pagehide/visibilitychange. Wirft nie. */
export async function flushDiagnose(keepalive = false): Promise<void> {
  try {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
    if (queue.length === 0) return;
    if (!isSendable()) {
      queue = [];
      return;
    }
    const now = Date.now();
    sentTimes = sentTimes.filter((t) => now - t < HOUR_MS);
    const budget = MAX_PER_HOUR - sentTimes.length;
    if (budget <= 0) {
      queue = [];
      return;
    }
    const limitBytes = keepalive ? MAX_KEEPALIVE_BYTES : MAX_BODY_BYTES;
    let batch = queue.slice(0, Math.min(MAX_PER_CALL, budget));
    let body = JSON.stringify({ errors: batch });
    while (body.length > limitBytes && batch.length > 1) {
      batch = batch.slice(0, batch.length - 1);
      body = JSON.stringify({ errors: batch });
    }
    if (body.length > limitBytes) {
      // Einzeleintrag zu groß: verwerfen
      queue = queue.slice(batch.length);
      return;
    }
    queue = queue.slice(batch.length);
    for (let i = 0; i < batch.length; i++) sentTimes.push(now);
    if (queue.length > 0) scheduleFlush();

    const correlationId = batch.find((e) => e.correlationId)?.correlationId;
    await fetch(DIAGNOSE_ENDPOINT, {
      method: 'POST',
      headers: buildHeaders(correlationId),
      body,
      keepalive,
      cache: 'no-store',
    }).catch(() => undefined);
  } catch { /* still */ }
}

/** Registriert Flush bei Tab-Wechsel/Seitenende. Einmaliger Aufruf beim App-Start. */
export function initDiagnose(): void {
  if (!diagnoseSchalter.aktiv || installed || typeof window === 'undefined') return;
  installed = true;
  const onHide = () => void flushDiagnose(true);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') onHide();
  });
  window.addEventListener('pagehide', onHide);
}

/** Test-Helfer. */
export function __resetDiagnose(): void {
  queue = [];
  sentTimes = [];
  recentKeys.clear();
  if (timer) clearTimeout(timer);
  timer = null;
}
